/**
 * Shared helpers for Rezdy / CMS HTML product descriptions.
 *
 * `normalizeProductHtmlForDisplay` is what {@link DisplayText} uses before
 * `html-react-parser` (single source of truth).
 *
 * `htmlProductDescriptionToPlainText` uses that same normalization, then produces
 * structured plain text (paragraphs, list bullets) for AI prompts and hero teasers.
 */

/**
 * Matches the historical product page behaviour: remove `<br>` tags before parsing
 * so layout comes from block elements (`p`, `ul`, etc.), not manual breaks.
 */
export function normalizeProductHtmlForDisplay(html: string): string {
    if (!html) return '';
    return html.replace(/<br\s*\/?>/gi, '');
}

/**
 * Plain text for hero teasers, AI prompts, and fallbacks. Uses the same
 * normalization as the product UI, then walks the DOM for readable structure.
 */
export function htmlProductDescriptionToPlainText(html: string): string {
    const normalized = normalizeProductHtmlForDisplay(html).trim();
    if (!normalized) return '';

    if (typeof document === 'undefined') {
        return normalized
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    const div = document.createElement('div');
    div.innerHTML = normalized;

    function extractFragment(nodes: NodeListOf<ChildNode> | ChildNode[]): string {
        let out = '';
        for (const node of Array.from(nodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                out += node.textContent ?? '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as Element;
                const tag = el.tagName.toLowerCase();

                if (tag === 'li') {
                    const inner = extractFragment(el.childNodes).trim();
                    out += `${out && !out.endsWith('\n') ? '\n' : ''}• ${inner}\n`;
                } else if (tag === 'p' || tag === 'div' || /^h[1-6]$/.test(tag)) {
                    const inner = extractFragment(el.childNodes).trim();
                    if (inner) out += `${inner}\n\n`;
                } else if (tag === 'br') {
                    out += '\n';
                } else if (tag === 'ul' || tag === 'ol') {
                    out += extractFragment(el.childNodes);
                } else {
                    out += extractFragment(el.childNodes);
                }
            }
        }
        return out;
    }

    return extractFragment(div.childNodes)
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
